import { useState, useEffect, useRef } from "preact/hooks";
import type { JSX } from "preact";

type ImageWithSkeletonProps = {
  src: string | null | undefined;
  alt: string;
  className?: string;
  containerClassName?: string;
  fallbackIcon?: JSX.Element;
  timeout?: number;
};

export function ImageWithSkeleton({
  src,
  alt,
  className = "",
  containerClassName = "",
  fallbackIcon,
  timeout = 5000,
}: ImageWithSkeletonProps) {
  const [state, setState] = useState<"loading" | "loaded" | "error">(
    src ? "loading" : "error"
  );
  const [showSkeleton, setShowSkeleton] = useState<boolean>(Boolean(src));
  const timeoutRef = useRef<number | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!src) {
      setState("error");
      setShowSkeleton(false);
      return;
    }

    // Check if image is already loaded (cached)
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setState("loaded");
      setShowSkeleton(false);
      return;
    }

    setState("loading");
    setShowSkeleton(true);

    // Stop shimmer after timeout, but keep image mounted to allow late loads.
    timeoutRef.current = window.setTimeout(() => {
      if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
        setState("loaded");
      }
      setShowSkeleton(false);
    }, timeout);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [src, timeout]);

  const handleLoad = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    setState("loaded");
    setShowSkeleton(false);
  };

  const handleError = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    setState("error");
    setShowSkeleton(false);
  };

  const shouldRevealImage = state === "loaded" || !showSkeleton;

  if (!src || state === "error") {
    return (
      <div className={`festapp-image-fallback ${containerClassName}`}>
        {fallbackIcon ?? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
        )}
      </div>
    );
  }

  return (
    <div className={`festapp-image-container ${containerClassName}`}>
      {state === "loading" && showSkeleton && <div className="festapp-image-skeleton" />}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`festapp-image-reveal ${shouldRevealImage ? "loaded" : ""} ${className}`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
